/**
 * 管理后台画布预览：直接复用画布真实渲染栈。
 *
 * - 节点用 NodeRenderer + 真实节点组件渲染，和用户画布视觉一致；
 * - 连线用 CanvasEdgeLayer，与 Board 共用同一套锚点/颜色逻辑；
 * - boardStore 进入 readOnly 预览态：挂载副作用与编辑动作全部丢弃，
 *   节点层 pointer-events: none，只保留外层拖拽平移 / 滚轮缩放。
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Loader2, Maximize2, ZoomIn, ZoomOut } from 'lucide-react';
import { boardActions, useBoard } from '../../../apps/board/state/boardStore';
import { NodeRenderer } from '../../../apps/board/nodes/NodeRenderer';
import { CanvasEdgeLayer } from '../../../apps/board/CanvasEdgeLayer';
import '../../../apps/board/board.css';
import type {
  CanvasBackground,
  CanvasEdge,
  CanvasNode,
  CanvasProjectFull,
  CanvasTheme,
} from '../../../apps/board/types';

export interface PreviewProject {
  id: string;
  name: string;
  viewportX: number;
  viewportY: number;
  zoom: number;
  background: CanvasBackground;
  theme: CanvasTheme;
  nodes: CanvasNode[];
  edges: CanvasEdge[];
}

interface BoardPreviewCanvasProps {
  project: PreviewProject;
  selectedNodeId: string | null;
}

function computeFitViewport(
  nodes: CanvasNode[],
  containerW: number,
  containerH: number,
): { x: number; y: number; zoom: number } | null {
  if (nodes.length === 0) return null;
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const n of nodes) {
    minX = Math.min(minX, n.x);
    minY = Math.min(minY, n.y);
    maxX = Math.max(maxX, n.x + n.width);
    maxY = Math.max(maxY, n.y + n.height);
  }
  const padding = 72;
  const contentW = maxX - minX;
  const contentH = maxY - minY;
  if (contentW <= 0 || contentH <= 0) return null;
  const zoom = Math.min(
    (containerW - padding * 2) / contentW,
    (containerH - padding * 2) / contentH,
    1.2,
  );
  const clampedZoom = Math.max(0.03, Math.min(zoom, 2));
  return {
    x: padding - minX * clampedZoom + (containerW - padding * 2 - contentW * clampedZoom) / 2,
    y: padding - minY * clampedZoom + (containerH - padding * 2 - contentH * clampedZoom) / 2,
    zoom: clampedZoom,
  };
}

export function BoardPreviewCanvas({ project, selectedNodeId }: BoardPreviewCanvasProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<{ startX: number; startY: number; vpX: number; vpY: number; active: boolean } | null>(null);

  const [viewport, setViewport] = useState({ x: 0, y: 0, zoom: 1 });

  const nodes = useBoard((s) => s.nodes);
  const edges = useBoard((s) => s.edges);
  const background = useBoard((s) => s.background);
  const theme = useBoard((s) => s.theme);
  // 灌入完成标记直接读 store，避免在 effect 里 setState 触发级联渲染
  const seeded = useBoard((s) => s.readOnly && s.projectId === project.id);

  useLayoutEffect(() => {
    boardActions.loadPreview({
      ...project,
      isHidden: false,
      createdAt: '',
      updatedAt: '',
    } as CanvasProjectFull);
    return () => {
      boardActions.reset();
    };
  }, [project]);

  const fitToContent = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const fit = computeFitViewport(nodes, rect.width, rect.height);
    if (fit) setViewport(fit);
  }, [nodes]);

  // 首次灌入后按内容适应屏幕；节点数变化（重新加载）也重新适配
  useEffect(() => {
    if (!seeded) return;
    const frame = requestAnimationFrame(fitToContent);
    return () => cancelAnimationFrame(frame);
  }, [seeded, fitToContent]);

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      vpX: viewport.x,
      vpY: viewport.y,
      active: true,
    };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  }, [viewport]);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    const drag = dragRef.current;
    if (!drag?.active) return;
    const dx = e.clientX - drag.startX;
    const dy = e.clientY - drag.startY;
    setViewport((vp) => ({ ...vp, x: drag.vpX + dx, y: drag.vpY + dy }));
  }, []);

  const onPointerUp = useCallback(() => {
    if (dragRef.current) dragRef.current.active = false;
    dragRef.current = null;
  }, []);

  const onWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const cx = e.clientX - rect.left;
    const cy = e.clientY - rect.top;
    setViewport((vp) => {
      const delta = -e.deltaY * 0.001;
      const newZoom = Math.max(0.05, Math.min(2, vp.zoom * (1 + delta)));
      const ratio = newZoom / vp.zoom;
      return {
        x: cx - (cx - vp.x) * ratio,
        y: cy - (cy - vp.y) * ratio,
        zoom: newZoom,
      };
    });
  }, []);

  const zoomBy = (factor: number) => {
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const cx = rect.width / 2;
    const cy = rect.height / 2;
    setViewport((vp) => {
      const newZoom = Math.max(0.05, Math.min(2, vp.zoom * factor));
      const ratio = newZoom / vp.zoom;
      return {
        x: cx - (cx - vp.x) * ratio,
        y: cy - (cy - vp.y) * ratio,
        zoom: newZoom,
      };
    });
  };

  const bgClass = background === 'grid'
    ? 'canvas-bg-grid'
    : background === 'blank'
      ? 'canvas-bg-blank'
      : 'canvas-bg-dot';

  const selected = selectedNodeId ? nodes.find((n) => n.id === selectedNodeId) : null;

  if (!seeded) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`absolute inset-0 overflow-hidden ${bgClass} ${theme === 'dark' ? 'canvas-theme-dark' : ''} cursor-grab active:cursor-grabbing`}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onWheel={onWheel}
    >
      {nodes.length === 0 && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm text-slate-400">
          这是一个空画布
        </div>
      )}

      {/* 只读渲染层：节点/连线不接收任何指针事件 */}
      <div
        className="pointer-events-none absolute left-0 top-0 origin-top-left"
        style={{ transform: `translate(${viewport.x}px, ${viewport.y}px) scale(${viewport.zoom})` }}
      >
        <CanvasEdgeLayer
          nodes={nodes}
          edges={edges}
          selectedEdgeIds={[]}
          highlightedNodeIds={[]}
          interactive={false}
        />
        <NodeRenderer
          nodes={nodes}
          selectedIds={[]}
          highlightedIds={[]}
          userCredits={999999}
        />
        {selected && (
          <div
            className="pointer-events-none absolute rounded-3xl border-2 border-blue-500 shadow-[0_0_0_1px_rgba(59,130,246,0.35)]"
            style={{
              left: selected.x,
              top: selected.y,
              width: selected.width,
              minHeight: selected.height,
              zIndex: 100000,
            }}
          />
        )}
      </div>

      {/* 底部缩放控制 */}
      <div className="absolute bottom-4 left-1/2 z-20 flex -translate-x-1/2 items-center gap-1 rounded-xl border border-slate-700 bg-slate-800/90 px-2 py-1.5 shadow-lg backdrop-blur-sm">
        <button
          onClick={() => zoomBy(1 / 1.2)}
          className="flex items-center justify-center rounded-lg p-1.5 text-slate-300 transition-colors hover:bg-slate-700 hover:text-white"
          title="缩小"
        >
          <ZoomOut className="h-4 w-4" />
        </button>
        <span className="min-w-[48px] text-center text-xs font-medium text-slate-300">
          {Math.round(viewport.zoom * 100)}%
        </span>
        <button
          onClick={() => zoomBy(1.2)}
          className="flex items-center justify-center rounded-lg p-1.5 text-slate-300 transition-colors hover:bg-slate-700 hover:text-white"
          title="放大"
        >
          <ZoomIn className="h-4 w-4" />
        </button>
        <div className="mx-1 h-4 w-px bg-slate-700" />
        <button
          onClick={fitToContent}
          className="flex items-center gap-1 rounded-lg px-2 py-1.5 text-xs text-slate-300 transition-colors hover:bg-slate-700 hover:text-white"
          title="适应屏幕"
        >
          <Maximize2 className="h-3.5 w-3.5" />
          适应屏幕
        </button>
      </div>
    </div>
  );
}
